import { FC, useCallback, useEffect, useState } from 'react';

import { debounce } from 'lodash';

import { useBreakpoint } from '@contexts/breakpoints';
import { callService } from '@api/caller';
import { AirportSearchViewModel } from '@api/index';
import { LocationPinIcon, PlaneIcon } from '@assets/icons';
import { FlexibleDiv } from '@components/formStyles';
import {
  ItemText,
  ItemTextCity,
  ItemTextWrapper,
} from '@components/ui/search/SearchResultListStyle';
import CustomIcon from '@components/utils/CustomIcon';
import makeBold from '@utils/makeBold';

import useFlightSearchBar from '../../context/flight';
import {
  AntAutoComplete,
  Arrow,
  ErrorMessage,
  ErrorMessageContainer,
  ItemErrorText,
  Label,
  OptionWrapper,
  SelectWrapper,
} from './styled';

export type AirPortTypes = AirportSearchViewModel | null;
type PropsTypes = {
  label: string;
  onChange?: (airport: AirPortTypes) => void;
  error?: boolean;
  isValueChange?: number;
  changedValue?: AirPortTypes | null;
};
const { Option } = AntAutoComplete;

const AirportAutoComplate: FC<PropsTypes> = ({
  label,
  onChange: pOnChange = (v) => v,
  error,
  isValueChange,
  changedValue,
}) => {
  const { state } = useFlightSearchBar();
  const { xs } = useBreakpoint();

  const [value, setValue] = useState<AirPortTypes>(null);
  const [inputValue, setInputValue] = useState('');
  const [data, setData] = useState<Array<AirportSearchViewModel> | []>([]);
  const [isFocus, setIsFocus] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [isAirportResultsLoading, setIsAirportResultsLoading] = useState(false);
  const [isDataFetched, setIsDataFetched] = useState(false);

  const handleSearch = (value: string) => {
    setInputValue(value);
    if (value === '') {
      setValue(null);
      pOnChange(null);
    }

    setIsDataFetched(false);
  };

  useEffect(() => {
    if (state.isSubmit) {
      setIsTouched(true);
    }
  }, [state.isSubmit]);

  const handleFocus = () => {
    setIsFocus(true);
    setIsTouched(true);
  };

  const handleBlur = () => {
    setIsFocus(false);
    setIsTouched(false);
  };

  const fetchData = async (s: string) => {
    try {
      setIsAirportResultsLoading(true);
      const airports = await callService('SearchesService', 'searchesFlight', {
        s: s,
      });
      setData(airports);
    } catch (error) {
      setData([]);
    } finally {
      setIsAirportResultsLoading(false);
      setIsDataFetched(true);
    }
  };

  const searchAirport = useCallback(
    debounce((s: string) => fetchData(s), 500),
    []
  );

  useEffect(() => {
    if (inputValue) {
      const isDifferentValue = value?.airportName !== inputValue;
      if (inputValue.length > 2 && isDifferentValue) searchAirport(inputValue);
    } else {
      setData([]);
    }
  }, [inputValue]);

  useEffect(() => {
    setValue(changedValue || null);
    pOnChange(changedValue as AirPortTypes);
    setInputValue(
      changedValue
        ? `${changedValue?.airportName || ''}${
            changedValue?.airportCode ? ', ' + changedValue?.airportCode : ''
          }`
        : ''
    );
  }, [isValueChange]);

  const selectValue = (option: AirPortTypes) => {
    setValue(option);
    pOnChange(option);
    setInputValue(
      `${option?.airportName || ''}${
        option?.airportCode ? ', ' + option?.airportCode : ''
      }`
    );
  };

  const isShowItemTextError =
    isDataFetched &&
    !isAirportResultsLoading &&
    inputValue.length > 2 &&
    data &&
    Array.isArray(data) &&
    !data.length;

  return (
    <SelectWrapper>
      <Label>{label}</Label>
      <AntAutoComplete
        onSearch={handleSearch}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={<div style={{ marginTop: 25 }}>Şehir veya Havalimanı</div>}
        allowClear
        isFocus={isFocus}
        isError={(error && isTouched) as boolean}
        value={inputValue}
        dropdownStyle={{
          minWidth: xs || xs !== undefined ? 'auto' : '400px',
        }}
      >
        {data && Array.isArray(data) && data.length ? (
          data?.map((option) => (
            <Option key={`${option.airportId}-${option.airportCode}`}>
              <OptionWrapper onClick={() => selectValue(option)}>
                <CustomIcon
                  fontSize={24}
                  paletteColor='primaryBrand'
                  icon={option.isCity ? LocationPinIcon : PlaneIcon}
                />
                <FlexibleDiv right={17} />
                <ItemTextWrapper>
                  {option.isCity ? (
                    <>
                      <ItemText
                        dangerouslySetInnerHTML={{
                          __html: makeBold(
                            `${option.cityName},${option.countryName}`,
                            inputValue
                          ),
                        }}
                      />
                      <ItemTextCity
                        dangerouslySetInnerHTML={{
                          __html: makeBold(`${option.airportName}`, inputValue),
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <ItemText
                        dangerouslySetInnerHTML={{
                          __html: makeBold(
                            `${option.airportName},${option.airportCode}`,
                            inputValue
                          ),
                        }}
                      />
                      <ItemTextCity
                        dangerouslySetInnerHTML={{
                          __html: makeBold(
                            `${option.cityName},${option.countryName}`,
                            inputValue
                          ),
                        }}
                      />
                    </>
                  )}
                </ItemTextWrapper>
              </OptionWrapper>
            </Option>
          ))
        ) : isShowItemTextError ? (
          <Option>
            <OptionWrapper>
              <ItemErrorText>
                {!xs || xs === undefined ? (
                  <span>Aradığınız kriterlere uygun sonuç bulunamadı.</span>
                ) : (
                  <span>
                    Aradığınız kriterlere uygun <br />
                    sonuç bulunamadı.
                  </span>
                )}
              </ItemErrorText>
            </OptionWrapper>
          </Option>
        ) : null}
      </AntAutoComplete>
      {!isFocus && isTouched && error && (
        <ErrorMessageContainer>
          <Arrow />
          <ErrorMessage>Şehir Adını Kontrol Ediniz</ErrorMessage>
        </ErrorMessageContainer>
      )}
    </SelectWrapper>
  );
};

export default AirportAutoComplate;
